import {defineMessages} from 'react-intl';
import type {BoUser} from '@igp/shared/contract/pam';
import {map, mergeMap} from '@igp/shared/otel';
import {BaseEpicsBuilder} from '@igp/shared/redux';
import type {INormalized} from '@igp/shared/redux/entity';
import type {Message} from '@igp/shared/redux/message';
import {RealtimeMessageTrigger} from '@igp/shared/redux/realtime';
import {EntityType} from '@models';
import {entityActions, messageActions, realtimeActions} from '@redux';
import {inject, injectable} from 'inversify';
import type {Epic} from 'redux-observable';
import {filter, of} from 'rxjs';
import {isActionOf} from 'typesafe-actions';

import {boUserActions} from './actions';
import {boUserServiceTypes} from './constants';
import type {IBoUserService} from './service';
import type {
    BoUserAddPayload,
    BoUserEditPayload,
    BoUserErrorType,
    BoUserResetPasswordPayload,
    BoUserUnlinkAgentPayload,
    BoUserUpdateAccountStatusPayload,
} from './types';

export const localized = defineMessages({
    addUserFailed: {
        id: 'boUser_addUserFailed',
        defaultMessage: 'Failed to add user',
    },
    updateUserSuccess: {
        id: 'boUser_updateUserSuccess',
        defaultMessage: 'User updated successfully',
    },
    updateUserFailed: {
        id: 'boUser_updateUserFailed',
        defaultMessage: 'Failed to update user',
    },
    resetPasswordFailed: {
        id: 'boUser_resetPasswordFailed',
        defaultMessage: 'Failed to reset password',
    },
    accountLockedSuccess: {
        id: 'boUser_accountLockedSuccess',
        defaultMessage: 'User account locked',
    },
    accountUnlockedSuccess: {
        id: 'boUser_accountUnlockedSuccess',
        defaultMessage: 'User account unlocked',
    },
    updateUserAccountStatusFailed: {
        id: 'boUser_updateUserAccountStatusFailed',
        defaultMessage: 'Failed to update user account status',
    },
    usernameAlreadyExists: {
        id: 'boUser_usernameAlreadyExists',
        defaultMessage: 'Username already exists',
    },
    emailAlreadyExists: {
        id: 'boUser_emailAlreadyExists',
        defaultMessage: 'Email already in use',
    },
    playerIdNotExists: {
        id: 'boUser_playerIdNotExists',
        defaultMessage: 'Player ID does not exist',
    },
    playerIdAlreadyLinked: {
        id: 'boUser_playerIdAlreadyLinked',
        defaultMessage: 'Player ID already linked to another user',
    },
    unlinkAgentSuccess: {
        id: 'boUser_unlinkAgentSuccess',
        defaultMessage: 'Agent unlinked successfully',
    },
    unlinkAgentFailed: {
        id: 'boUser_unlinkAgentFailed',
        defaultMessage: 'Failed to unlink agent',
    },
    boUserNotExists: {
        id: 'boUser_boUserNotExists',
        defaultMessage: 'User does not exist',
    },
    boUserIsNotAgent: {
        id: 'boUser_boUserIsNotAgent',
        defaultMessage: 'User is not an agent',
    },
});

@injectable()
export class BoUserEpicsBuilder extends BaseEpicsBuilder {
    constructor(@inject(boUserServiceTypes.service) private readonly _service: IBoUserService) {
        super();
    }

    protected buildEpicList(): Epic[] {
        return [
            this.buildAddUserRequestEpic(),
            this.buildAddUserSuccessEpic(),
            this.buildAddUserFailureEpic(),
            this.buildEditUserRequestEpic(),
            this.buildEditUserSuccessEpic(),
            this.buildEditUserFailureEpic(),
            this.buildResetPasswordRequestEpic(),
            this.buildResetPasswordFailureEpic(),
            this.buildUpdateAccountStatusRequestEpic(),
            this.buildUpdateAccountStatusSuccessEpic(),
            this.buildUpdateAccountStatusFailureEpic(),
            this.buildUnlinkAgentRequestEpic(),
            this.buildUnlinkAgentSuccessEpic(),
            this.buildUnlinkAgentFailureEpic(),
        ];
    }

    private buildAddUserRequestEpic(): Epic {
        return this.buildRequestEpic(boUserActions.addUser, (payload: BoUserAddPayload) => this._service.addBoUser(payload));
    }

    private buildAddUserSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.addUser.success)),
                map(() => realtimeActions.event({entity: EntityType.BoUser, items: [], trigger: {type: RealtimeMessageTrigger.Add}}))
            );
    }

    private buildAddUserFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.addUser.failure)),
                map(response => {
                    const error = response.payload?.errors?.[0];
                    return messageActions.saveMessage(
                        this.getErrorByCode(error?.code as BoUserErrorType) ?? {
                            message: localized.addUserFailed,
                            mode: 'error',
                        }
                    );
                })
            );
    }

    private buildEditUserRequestEpic(): Epic {
        return this.buildRequestEpic(boUserActions.editUser, (payload: BoUserEditPayload) => this._service.updateBoUser(payload));
    }

    private buildEditUserSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.editUser.success)),
                mergeMap(() => {
                    return of(
                        messageActions.saveMessage({message: localized.updateUserSuccess, mode: 'success'}),
                        realtimeActions.event({entity: EntityType.BoUser, items: [], trigger: {type: RealtimeMessageTrigger.Update}})
                    );
                })
            );
    }

    private buildEditUserFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.editUser.failure)),
                map(response => {
                    const error = response.payload?.errors?.[0];
                    return messageActions.saveMessage(
                        this.getErrorByCode(error?.code as BoUserErrorType) ?? {
                            message: localized.updateUserFailed,
                            mode: 'error',
                        }
                    );
                })
            );
    }

    private buildResetPasswordRequestEpic(): Epic {
        return this.buildRequestEpic(boUserActions.resetPassword, (payload: BoUserResetPasswordPayload) =>
            this._service.resetPassword(payload)
        );
    }

    private buildResetPasswordFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.resetPassword.failure)),
                map(() => messageActions.saveMessage({message: localized.resetPasswordFailed, mode: 'error'}))
            );
    }

    private buildUpdateAccountStatusRequestEpic(): Epic {
        return this.buildRequestEpic(boUserActions.updateUserAccountStatus, (payload: BoUserUpdateAccountStatusPayload) =>
            this._service.updateAccountStatus(payload)
        );
    }

    private buildUpdateAccountStatusSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.updateUserAccountStatus.success)),
                mergeMap(action => {
                    const {isLocked} = action.payload.requestPayload;
                    return of(
                        messageActions.saveMessage({
                            message: isLocked ? localized.accountLockedSuccess : localized.accountUnlockedSuccess,
                            mode: 'success',
                        }),
                        entityActions.updateItem({
                            type: EntityType.BoUser,
                            id: action.payload.requestPayload.boUserId,
                            updatedItem: {
                                enabled: !isLocked,
                            } satisfies Partial<BoUser> as INormalized,
                        })
                    );
                })
            );
    }

    private buildUpdateAccountStatusFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.updateUserAccountStatus.failure)),
                map(() => messageActions.saveMessage({message: localized.updateUserAccountStatusFailed, mode: 'error'}))
            );
    }

    private buildUnlinkAgentRequestEpic(): Epic {
        return this.buildRequestEpic(boUserActions.unlinkAgent, (payload: BoUserUnlinkAgentPayload) => this._service.unlinkAgent(payload));
    }

    private buildUnlinkAgentSuccessEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.unlinkAgent.success)),
                mergeMap(action => {
                    return of(
                        messageActions.saveMessage({message: localized.unlinkAgentSuccess, mode: 'success'}),
                        entityActions.updateItem({
                            type: EntityType.BoUser,
                            id: action.payload.requestPayload.boUserId,
                            updatedItem: {
                                attributes: null,
                                groups: null,
                            } satisfies Partial<BoUser> as INormalized,
                        })
                    );
                })
            );
    }

    private buildUnlinkAgentFailureEpic(): Epic {
        return action$ =>
            action$.pipe(
                filter(isActionOf(boUserActions.unlinkAgent.failure)),
                map(action => {
                    const error = action.payload?.errors?.[0];
                    return messageActions.saveMessage(
                        this.getErrorByCode(error?.code as BoUserErrorType) ?? {
                            message: localized.unlinkAgentFailed,
                            mode: 'error',
                        }
                    );
                })
            );
    }

    private getErrorByCode(errorCode: BoUserErrorType): Message {
        const errorMap: Partial<Record<BoUserErrorType, Message>> = {
            USERNAME_ALREADY_EXISTS: {
                mode: 'error',
                message: localized.usernameAlreadyExists,
            },
            EMAIL_ALREADY_EXISTS: {
                mode: 'error',
                message: localized.emailAlreadyExists,
            },
            PLAYER_ID_NOT_EXISTS: {
                mode: 'error',
                message: localized.playerIdNotExists,
            },
            PLAYER_ID_ALREADY_LINKED: {
                mode: 'error',
                message: localized.playerIdAlreadyLinked,
            },
            BO_USER_NOT_EXISTS: {
                mode: 'error',
                message: localized.boUserNotExists,
            },
            BO_USER_IS_NOT_AGENT: {
                mode: 'error',
                message: localized.boUserIsNotAgent,
            },
        };
        return errorMap[errorCode];
    }
}
